/**
 * 에러 메시지 관리 유틸리티
 * 사용자 친화적인 에러 메시지 제공
 */

export enum ErrorType {
    NETWORK_ERROR = 'NETWORK_ERROR',
    TOKEN_EXPIRED = 'TOKEN_EXPIRED',
    TOKEN_REFRESH_FAILED = 'TOKEN_REFRESH_FAILED',
    UNAUTHORIZED = 'UNAUTHORIZED',
    FORBIDDEN = 'FORBIDDEN',
    NOT_FOUND = 'NOT_FOUND',
    SERVER_ERROR = 'SERVER_ERROR',
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface ErrorMessage {
    title: string;
    message: string;
    action?: string;
    severity: 'info' | 'warning' | 'error';
}

/**
 * 에러 타입별 메시지 매핑
 */
const ERROR_MESSAGES: Record<ErrorType, ErrorMessage> = {
    [ErrorType.NETWORK_ERROR]: {
        title: '네트워크 오류',
        message: '인터넷 연결을 확인하고 다시 시도해주세요.',
        action: '다시 시도',
        severity: 'warning'
    },

    [ErrorType.TOKEN_EXPIRED]: {
        title: '세션 만료',
        message: '로그인 세션이 만료되었습니다. 다시 로그인해주세요.',
        action: '로그인',
        severity: 'info'
    },

    [ErrorType.TOKEN_REFRESH_FAILED]: {
        title: '인증 갱신 실패',
        message: '인증 정보 갱신에 실패했습니다. 다시 로그인해주세요.',
        action: '로그인',
        severity: 'error'
    },

    [ErrorType.UNAUTHORIZED]: {
        title: '인증 필요',
        message: '이 기능을 사용하려면 로그인이 필요합니다.',
        action: '로그인',
        severity: 'warning'
    },

    [ErrorType.FORBIDDEN]: {
        title: '접근 권한 없음',
        message: '이 기능에 접근할 권한이 없습니다.',
        severity: 'error'
    },

    [ErrorType.NOT_FOUND]: {
        title: '요청한 정보를 찾을 수 없음',
        message: '요청하신 정보를 찾을 수 없습니다.',
        action: '새로고침',
        severity: 'warning'
    },

    [ErrorType.SERVER_ERROR]: {
        title: '서버 오류',
        message: '서버에서 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        action: '다시 시도',
        severity: 'error'
    },

    [ErrorType.VALIDATION_ERROR]: {
        title: '입력 정보 오류',
        message: '입력하신 정보를 확인해주세요.',
        severity: 'warning'
    },

    [ErrorType.UNKNOWN_ERROR]: {
        title: '알 수 없는 오류',
        message: '예상치 못한 오류가 발생했습니다. 관리자에게 문의해주세요.',
        severity: 'error'
    }
};

/**
 * HTTP 상태 코드를 에러 타입으로 변환
 */
export function getErrorTypeFromStatus(status: number): ErrorType {
    switch (status) {
        case 401:
            return ErrorType.TOKEN_EXPIRED;
        case 403:
            return ErrorType.FORBIDDEN;
        case 404:
            return ErrorType.NOT_FOUND;
        case 422:
            return ErrorType.VALIDATION_ERROR;
        case 500:
        case 502:
        case 503:
        case 504:
            return ErrorType.SERVER_ERROR;
        default:
            return ErrorType.UNKNOWN_ERROR;
    }
}

/**
 * 에러 객체에서 에러 타입 추출
 */
export function getErrorTypeFromError(error: any): ErrorType {
    // 네트워크 오류 확인
    if (!error.response) {
        return ErrorType.NETWORK_ERROR;
    }

    // HTTP 상태 코드 기반
    if (error.response?.status) {
        return getErrorTypeFromStatus(error.response.status);
    }

    // 에러 메시지 기반
    if (error.message) {
        const message = error.message.toLowerCase();
        if (message.includes('token') || message.includes('인증')) {
            return ErrorType.TOKEN_EXPIRED;
        }
        if (message.includes('network') || message.includes('네트워크')) {
            return ErrorType.NETWORK_ERROR;
        }
    }

    return ErrorType.UNKNOWN_ERROR;
}

/**
 * 에러 타입에 따른 메시지 반환
 */
export function getErrorMessage(errorType: ErrorType): ErrorMessage {
    return ERROR_MESSAGES[errorType];
}

/**
 * 에러 객체에서 사용자 친화적 메시지 생성
 */
export function createUserFriendlyError(error: any): ErrorMessage {
    const errorType = getErrorTypeFromError(error);
    const baseMessage = getErrorMessage(errorType);

    // 서버에서 제공한 메시지가 있으면 추가
    if (error.response?.data?.message) {
        return {
            ...baseMessage,
            message: `${baseMessage.message} (${error.response.data.message})`
        };
    }

    return baseMessage;
}

/**
 * 토스트 메시지용 간단한 에러 메시지
 */
export function getSimpleErrorMessage(error: any): string {
    const errorMessage = createUserFriendlyError(error);
    return errorMessage.message;
}

/**
 * 에러 로깅용 상세 정보
 */
export function getErrorDetails(error: any): any {
    return {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        currentUrl: window.location.href
    };
}

const errorMessageUtils = {
    ErrorType,
    getErrorTypeFromStatus,
    getErrorTypeFromError,
    getErrorMessage,
    createUserFriendlyError,
    getSimpleErrorMessage,
    getErrorDetails
};

export default errorMessageUtils;
